import BoxWrapper from "../../components/ui/BoxWrapper";

import {
  DayPeriodFilter,
  formatCurrency,
  MonthPeriodFilter,
  WeekPeriodFilter,
  YearPeriodFilter,
} from "../../helpers/general";
import RestrictedRoute from "../auth/RestrictedRoute";
import MoneyIcon from "../../components/icons/taxiStatsIcons/MoneyIcon";
import PercentIcon from "../../components/icons/taxiStatsIcons/PercentIcon";
import TaxiIcon from "../../components/icons/taxiStatsIcons/TaxiIcon";
import DriversIcon from "../../components/icons/taxiStatsIcons/DriversIcon";
import {
  faArrowTrendDown,
  faArrowTrendUp,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { apiConfig } from "../../config/apiConfig";
import { useChartData } from "../../helpers/chartsStat";
import TopDriversChart from "../../components/taxi/Top_drivers_chart";
import { useCookie } from "../../hooks/useCookie";
import apiService from "../../service/apiService";
import AllDriversList from "../../components/taxi/Full_drivers_statistic";
import { Medal } from "lucide-react";
import AllUsersList from "../../components/userDetail/Full_users_statistic";
import TopUsersChart from "../../components/userDetail/Top_users_chart";
import Switch from "../../components/inputs/switch";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

// interface TaxiStatisticsData {
//   taxiStatistics: TaxiStatistics[];
// }

type TopDriversResponse = {
  drivers: Driver[];
  count: {
    totalApprovedDrivers: string;
    averageRating: string;
  };
};

type UsersStatisticsResponse = {
  users: User[];
  count: {
    totalApprovedUsers: string;
    averageRating: string;
  };
};

export default function Statistics() {
  const { token } = useCookie("vToken");
  const [page, setPage] = useState(1);
  const [userPage, setUserPage] = useState(1);
  const [search, setSearch] = useState("");
  const [currentFilter, setCurrentFilter] = useState(() => {
    const filter = new WeekPeriodFilter();
    filter.setValue(new Date());
    return filter;
  });
  const [activeDriversTab, setActiveDriversTab] = useState(0);
  const [activeUsersTab, setActiveUsersTab] = useState(0);
  const [type, setType] = useState("rides"); // "rides" or "spending"

  const [AllDriversData, setAllDriversData] = useState<any>(null);
  const [AllUsersStatisticsdata, setAllUsersStatisticsData] =
    useState<any>(null);

  const taxiStatistics = useQuery<TaxiStatistics>(
    ["getTaxiStatistics", currentFilter],
    () => {
      return apiService.MakeGetRequest(
        apiConfig.taxiStatistics.all({
          startDate: currentFilter.startDate.toISOString().split("T")[0],
          endDate: currentFilter.endDate.toISOString().split("T")[0],
        }),
        token,
      );
    },
  );

  const AllDriversQuery = useQuery(
    ["getTopDrivers", currentFilter, page, search],
    async () => {
      const res: TopDriversResponse = await apiService.MakeGetRequest(
        apiConfig.taxiStatistics.topDrivers({
          limit: 6,
          page,
          search,
        }),
        token,
      );
      return res;
    },
    { enabled: !!token },
  );

  const AllUsersStatistics = useQuery(
    ["getAllUsersStatistics", userPage],
    async () => {
      const res: UsersStatisticsResponse = await apiService.MakeGetRequest(
        apiConfig.userStatistics.all({
          limit: 6,
          page: userPage,
          search,
        }),
        token,
      );
      return res;
    },
    { enabled: !!token },
  );

  const TopUsersData = useQuery(
    ["getTopUsers", type],
    async () => {
      const res = await apiService.MakeGetRequest(
        apiConfig.userStatistics.topUsers({
          type,
          limit: 8,
        }),
        token,
      );
      return res;
    },
    { enabled: !!token },
  );

  // console.log("TopUsersData", TopUsersData);

  useEffect(() => {
    if (AllDriversQuery.data) {
      setAllDriversData({
        drivers: AllDriversQuery.data,
      });
    }
  }, [AllDriversQuery.data, page, search]);

  useEffect(() => {
    if (AllUsersStatistics.data) {
      setAllUsersStatisticsData(AllUsersStatistics.data);
    }
  }, [AllUsersStatistics.data]);

  useEffect(() => {
    const timer = setTimeout(() => {
      AllDriversQuery.refetch();
    }, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const [activeFilterType, setActiveFilterType] = useState("week");

  const createFilter = (type = "week") => {
    switch (type) {
      case "day":
        return new DayPeriodFilter();
      case "week":
        return new WeekPeriodFilter();
      case "month":
        return new MonthPeriodFilter();
      case "year":
        return new YearPeriodFilter();
      default:
        return new DayPeriodFilter();
    }
  };

  const handleFilterChange = (filterType = "week") => {
    setActiveFilterType(filterType);
    const newFilter = createFilter(filterType);
    newFilter.setValue(new Date());
    setCurrentFilter(newFilter);
    setPage(1);
  };

  const goToNextPeriod = () => {
    const nextPeriod = currentFilter.getNextPeriod();
    const newFilter = createFilter(activeFilterType);
    newFilter.setValue(nextPeriod);
    setCurrentFilter(newFilter);
    setPage(1);
  };

  const goToPreviousPeriod = () => {
    const prevPeriod = currentFilter.getPreviousPeriod();
    const newFilter = createFilter(activeFilterType);
    newFilter.setValue(prevPeriod);
    setCurrentFilter(newFilter);
    setPage(1);
  };

  const {
    revenueData,
    commissionData,
    ridesData,
    driversData,
    isLoading: chartsLoading,
    error: chartsError,
  } = useChartData(currentFilter, token);

  // console.log("driversData📛📛📛📛📛📛:", driversData);

  // console.log("AllDriversData📛📛📛📛📛📛:", AllDriversData?.drivers);

  // Helper function to check if a date string is weekend
  const isWeekendFromDateString = (dateString: string): boolean => {
    // dateString is in format "DD/MM/YYYY"
    const [day, month, year] = dateString.split("/");
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
  };

  const shouldApplyWeekendColors = (): boolean => {
    return currentFilter.id === "week" || currentFilter.id === "month";
  };

  const getMonthName = (dateString: string): string => {
    const [day, month, year] = dateString.split("/");
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

    const monthNamesFr = [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre",
    ];

    const monthIndex = date.getMonth();
    return `${monthNamesFr[monthIndex]}`;
  };

  const lineOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "MAD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(context.parsed.y);
            }

            // Add weekend indicator in tooltip
            const dateLabel = context.label || "";
            if (
              shouldApplyWeekendColors() &&
              isWeekendFromDateString(dateLabel)
            ) {
              label += " (Week-end)";
            }

            return label;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          callback: function (value, index, ticks) {
            const label = this.getLabelForValue(value as number);

            if (currentFilter.id === "year") {
              return getMonthName(label);
            }
            return label;
          },
          color: function (context) {
            const label = context.chart.data.labels?.[context.index] as string;
            return shouldApplyWeekendColors() && isWeekendFromDateString(label)
              ? "#FF7722"
              : "#666666";
          },
          font: function (context) {
            const label = context.chart.data.labels?.[context.index] as string;
            return {
              weight:
                shouldApplyWeekendColors() && isWeekendFromDateString(label)
                  ? "bold"
                  : "normal",
            };
          },
        },
      },
    },
  };

  const barOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.dataset.label === "Montant total (MAD)") {
              label += new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "MAD",
              }).format(context.parsed.y);
            } else {
              label += context.parsed.y;
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          callback: function (value, index, ticks) {
            const label = this.getLabelForValue(value as number);

            if (currentFilter.id === "year") {
              return getMonthName(label);
            }
            return label;
          },
          color: function (context) {
            const label = context.chart.data.labels?.[context.index] as string;
            return shouldApplyWeekendColors() && isWeekendFromDateString(label)
              ? "#FF7722"
              : "#666666";
          },
          font: function (context) {
            const label = context.chart.data.labels?.[context.index] as string;
            return {
              weight:
                shouldApplyWeekendColors() && isWeekendFromDateString(label)
                  ? "bold"
                  : "normal",
            };
          },
        },
      },
      y: {
        beginAtZero: true,
        position: "left",
        title: { display: true, text: "Courses" },
      },
      y1: {
        beginAtZero: true,
        position: "right",
        grid: { drawOnChartArea: false },
        title: { display: true, text: "Montant total (MAD)" },
      },
    },
  };

  if (taxiStatistics.isLoading) {
    return null;
  }

  if (taxiStatistics.isError) {
    return <div>Error fetching data</div>;
  }

  return (
    <RestrictedRoute>
      <h5 className="fs-4 fw-bold mb-2"> </h5>
      <BoxWrapper>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold px-4 py-4">Filtres</h2>

          <div className="d-flex flex-column flex-md-row px-2 px-md-4 justify-content-center align-items-center gap-2 gap-md-3 w-100 mx-auto">
            {/* Filter Buttons Container */}
            <div className="d-flex flex-wrap justify-content-center align-items-center gap-2 gap-md-3 mb-3 mb-md-0">
              <button
                onClick={() => handleFilterChange("day")}
                className={`rounded-sm py-2 px-3 d-flex align-items-center gap-2 justify-content-center shadow-primary border-0 text-primary flex-shrink-0
        ${
          activeFilterType === "day"
            ? "bg-gradient-primary text-white"
            : "bg-grey text-black"
        }
      `}
              >
                Jour
              </button>

              <button
                type="button"
                className={`rounded-sm py-2 px-3 d-flex align-items-center gap-2 justify-content-center shadow-primary border-0 text-primary flex-shrink-0
        ${
          activeFilterType === "week"
            ? "bg-gradient-primary text-white"
            : "bg-grey text-black"
        }
      `}
                onClick={() => handleFilterChange("week")}
              >
                Semaine
              </button>

              <button
                type="button"
                className={`rounded-sm py-2 px-3 d-flex align-items-center gap-2 justify-content-center shadow-primary border-0 text-primary flex-shrink-0
        ${
          activeFilterType === "month"
            ? "bg-gradient-primary text-white"
            : "bg-grey text-black"
        }
      `}
                onClick={() => handleFilterChange("month")}
              >
                Mois
              </button>

              <button
                type="button"
                className={`rounded-sm py-2 px-3 d-flex align-items-center gap-2 justify-content-center shadow-primary border-0 text-primary flex-shrink-0
        ${
          activeFilterType === "year"
            ? "bg-gradient-primary text-white"
            : "bg-grey text-black"
        }
      `}
                onClick={() => handleFilterChange("year")}
              >
                Année
              </button>
            </div>

            {/* Navigation Container */}
            <div className="d-flex align-items-center justify-content-center gap-2 gap-md-3 flex-shrink-0">
              <button
                onClick={goToPreviousPeriod}
                className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300 d-flex align-items-center justify-content-center"
                style={{ minWidth: "40px", minHeight: "40px" }}
              >
                ←
              </button>

              <div
                className="text-center text-md-start"
                style={{ minWidth: "120px" }}
              >
                <span className="text-lg font-medium d-block">
                  {currentFilter.getDisplayValue()}
                </span>
              </div>

              <button
                onClick={goToNextPeriod}
                className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300 d-flex align-items-center justify-content-center"
                style={{ minWidth: "40px", minHeight: "40px" }}
              >
                →
              </button>
            </div>
          </div>
        </div>

        <div className="container-fluid py-4 px-3 px-md-5">
          <div className="row g-3 g-md-4 justify-content-center">
            {/* Revenue Card */}
            {taxiStatistics.isLoading ? (
              <SkeletonLoader />
            ) : (
              <CardStatistics
                title="Revenue"
                total={taxiStatistics.data?.revenu.total ?? 0}
                previousTotal={taxiStatistics.data?.revenu.previousTotal ?? 0}
                icon={
                  (
                    <MoneyIcon width="48" height="48" className="text-danger" />
                  ) as React.ReactNode
                }
                borderColor="border-danger"
                iconBgColor="bg-danger"
                showCurrency={true}
                periodSelected={currentFilter}
              />
            )}

            {/* Commission Card */}
            {taxiStatistics.isLoading ? (
              <SkeletonLoader />
            ) : (
              <CardStatistics
                title="Commission"
                total={taxiStatistics.data?.commission.total ?? 0}
                previousTotal={
                  taxiStatistics.data?.commission.previousTotal ?? 0
                }
                icon={
                  (
                    <PercentIcon
                      width="48"
                      height="48"
                      className="text-primary"
                    />
                  ) as React.ReactNode
                }
                borderColor="border-primary"
                iconBgColor="bg-primary"
                showCurrency={true}
                periodSelected={currentFilter}
              />
            )}

            {/* Rides Card */}
            {taxiStatistics.isLoading ? (
              <SkeletonLoader />
            ) : (
              <CardStatistics
                title="Rides"
                total={taxiStatistics.data?.rides.total ?? 0}
                previousTotal={taxiStatistics.data?.rides.previousTotal ?? 0}
                icon={
                  (
                    <TaxiIcon width="48" height="48" className="text-danger" />
                  ) as React.ReactNode
                }
                borderColor="border-danger"
                iconBgColor="bg-danger"
                showCurrency={false}
                periodSelected={currentFilter}
              />
            )}

            {/* Drivers Card */}
            {taxiStatistics.isLoading ? (
              <SkeletonLoader />
            ) : (
              <CardStatistics
                title="Drivers"
                total={taxiStatistics.data?.drivers.total ?? 0}
                previousTotal={taxiStatistics.data?.drivers.previousTotal ?? 0}
                icon={
                  (
                    <DriversIcon
                      width="48"
                      height="48"
                      className="text-primary"
                    />
                  ) as React.ReactNode
                }
                borderColor="border-primary"
                iconBgColor="bg-primary"
                showCurrency={false}
                periodSelected={currentFilter}
              />
            )}
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {taxiStatistics.isLoading ? (
            <SkeletonLoader />
          ) : (
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="font-medium mb-4">Aperçu des revenus</h3>
              <div style={{ height: "300px" }}>
                {revenueData && (
                  <Line data={revenueData} options={lineOptions} />
                )}
              </div>
            </div>
          )}

          {taxiStatistics.isLoading ? (
            <SkeletonLoader />
          ) : (
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="font-medium mb-4">Aperçu des commissions</h3>
              <div style={{ height: "300px" }}>
                {commissionData && (
                  <Line data={commissionData} options={lineOptions} />
                )}
              </div>
            </div>
          )}
        </div>

        {taxiStatistics.isLoading ? (
          <SkeletonLoader />
        ) : (
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="font-medium mb-4">Nombre de courses</h3>
            <div style={{ height: "300px" }}>
              {ridesData && <Bar data={ridesData} options={barOptions} />}
            </div>
          </div>
        )}

        {/* Fixed Top Drivers Chart */}

        <div className="bg-white p-4 rounded-lg shadow-0 flex flex-col items-center">
          <h3 className="font-medium mb-4 mt-16"> 🏆 Meilleurs chauffeurs</h3>
          <div>
            <div className="d-flex justify-content-end mb-4">
              <button
                className="d-flex justify-content-end rounded p-2"
                onClick={() =>
                  activeDriversTab === 0
                    ? setActiveDriversTab(1)
                    : setActiveDriversTab(0)
                }
              >
                {activeDriversTab === 0 ? (
                  "Voir Tous --->"
                ) : (
                  <>
                    Voir les meilleurs
                    <Medal style={{ color: "#FF7722", marginRight: "6px" }} />
                  </>
                )}
              </button>
            </div>
            {activeDriversTab === 0 ? (
              <div>
                <TopDriversChart drivers={driversData} barColor="#2caaff" />
              </div>
            ) : (
              <div>
                <AllDriversList
                  totalitems={AllDriversData?.drivers?.meta?.totalItems || 0}
                  drivers={AllDriversData?.drivers?.drivers || []}
                  currentPage={AllDriversData?.drivers?.meta?.currentPage || 1}
                  totalPages={AllDriversData?.drivers?.meta?.totalPages || 1}
                  itemsPerPage={
                    AllDriversData?.drivers?.meta?.itemsPerPage || 10
                  }
                  onPageChange={(page) => setPage(page)}
                  search={search}
                  onSearchChange={setSearch}
                />
              </div>
            )}

            {/* Message si aucune donnée */}
            {!chartsLoading &&
              !chartsError &&
              driversData &&
              Array.isArray(driversData.labels) &&
              driversData.labels.length === 0 && (
                <div
                  className="text-center text-gray-500 py-8"
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 3,
                    background: "rgba(255,255,255,0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Aucune donnée disponible pour cette période
                </div>
              )}
          </div>
        </div>

        {/* //////////////////////////////// Users Statistics //////////////////////////////// */}

        <div
          style={{ marginTop: "3rem" }}
          className="bg-white p-4 rounded-lg shadow-md flex flex-col items-center border-t border-gray-200 "
        >
          <h3 className="font-medium mb-4"> 🎯 Meilleurs Utilisateurs</h3>
          <div>
            <div className="d-flex justify-content-end mb-4">
              <button
                className="d-flex justify-content-end rounded p-2"
                onClick={() =>
                  activeUsersTab === 0
                    ? setActiveUsersTab(1)
                    : setActiveUsersTab(0)
                }
              >
                {activeUsersTab === 0 ? (
                  "Voir Tous  --->"
                ) : (
                  <>
                    Voir les meilleurs
                    <Medal style={{ color: "#427A43", marginRight: "6px" }} />
                  </>
                )}
              </button>
            </div>
            {activeUsersTab === 0 ? (
              <div>
                <Switch
                  label=""
                  data={[
                    { label: "Courses", value: "rides" },
                    { label: "Reservations", value: "reservations" },
                  ]}
                  value={type}
                  onChange={(value) => setType(value)}
                  className=""
                  activeColor="bg-orange-accent"
                />
                <TopUsersChart
                  users={TopUsersData.data!}
                  barColor="#2caaff"
                  type={type}
                />
              </div>
            ) : (
              <div>
                <AllUsersList
                  totalitems={AllUsersStatisticsdata?.meta?.totalItems || 0}
                  users={AllUsersStatisticsdata?.items || []}
                  currentPage={AllUsersStatisticsdata?.meta?.currentPage || 1}
                  totalPages={AllUsersStatisticsdata?.meta?.totalPages || 1}
                  itemsPerPage={
                    AllUsersStatisticsdata?.meta?.itemsPerPage || 10
                  }
                  onPageChange={(page) => setUserPage(page)}
                />
              </div>
            )}

            {/* <Bar
              data={
                driversData && driversData.labels && driversData.datasets
                  ? driversData
                  : {
                      labels: [],
                      datasets: [
                        {
                          label: "Revenu",
                          data: drivers.map((d: any) =>
                            Number(d.total_revenue),
                          ),
                          backgroundColor: drivers.map(
                            (_: any, i: number) =>
                              [
                                "#22aaff",
                                "#f59e42",
                                "#74b9ff",
                                "#ff9a56",
                                "#6c5ce7",
                                "#00b894",
                                "#fd79a8",
                              ][i % 7],
                          ),
                          borderRadius: 30,
                          barPercentage: 0.5,
                          categoryPercentage: 0.9,
                        },
                      ],
                    }
              }
              options={topDriversBarOptions}
              plugins={[driverBarPlugin]}
            /> */}
            {/* Message si aucune donnée */}
            {!chartsLoading &&
              !chartsError &&
              driversData &&
              Array.isArray(driversData.labels) &&
              driversData.labels.length === 0 && (
                <div
                  className="text-center text-gray-500 py-8"
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 3,
                    background: "rgba(255,255,255,0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Aucune donnée disponible pour cette période
                </div>
              )}
          </div>
        </div>

        <div className="row text-center mb-4"></div>
      </BoxWrapper>
    </RestrictedRoute>
  );
}

interface CardStatisticsProps {
  title: string;
  total: number;
  previousTotal: number;
  icon: React.ReactNode;
  iconBgColor: string;
  borderColor: string;
  showCurrency: boolean;
  periodSelected:
    | WeekPeriodFilter
    | MonthPeriodFilter
    | YearPeriodFilter
    | DayPeriodFilter;
}

function CardStatistics({
  showCurrency,
  title,
  total,
  previousTotal,
  icon,
  iconBgColor,
  borderColor,
  periodSelected,
}: CardStatisticsProps) {
  const percentage =
    previousTotal === 0
      ? total > 0
        ? 100
        : 0
      : ((total - previousTotal) / previousTotal) * 100;

  return (
    <div className="col-12 col-sm-6 col-lg-3 ">
      <div
        className={`card rounded-4 overflow-hidden position-relative shadow p-3 mb-4 rounded-5  border ${borderColor}`}
      >
        <div className="card-body p-2">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div
              className={`rounded-circle ${iconBgColor} bg-opacity-10 p-3 d-flex align-items-center justify-content-center`}
            >
              {icon}
            </div>
            <div className="text-end">
              <h6 className="text-black mb-1 fw-normal">{title}</h6>
              <h3 className="fw-bold mb-0 text-black">
                {formatCurrency(total ?? 0)}
                {showCurrency && " MAD"}
              </h3>
            </div>
          </div>

          <div className="d-flex align-items-center bg-light justify-content-center  rounded-3 py-2 px-3 ">
            {percentage >= 0 ? (
              <FontAwesomeIcon
                icon={faArrowTrendUp}
                className="text-success me-1"
              />
            ) : (
              <FontAwesomeIcon
                icon={faArrowTrendDown}
                className="text-red me-1"
              />
            )}

            {percentage >= 0 ? (
              <span className="fw-semibold text-success me-1">
                {percentage.toFixed(2)}%
              </span>
            ) : (
              <span className="fw-semibold text-red me-1">
                {percentage.toFixed(2)}%
              </span>
            )}
            <span className="text-muted small">{periodSelected.name}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const SkeletonLoader = () => (
  <div className="col-12 col-sm-6 col-lg-3">
    <div className="card rounded-4 overflow-hidden position-relative shadow p-3 mb-4 rounded-5 border">
      <div className="card-body p-2">
        <div className="d-flex align-items-center justify-content-between mb-2">
          {/* Icon skeleton */}
          <div
            className="rounded-circle bg-light p-3 d-flex align-items-center justify-content-center"
            style={{
              width: "60px",
              height: "60px",
              backgroundColor: "#e0e0e0",
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          ></div>

          {/* Title and amount skeleton */}
          <div className="text-end" style={{ flex: 1, marginLeft: "12px" }}>
            {/* Title skeleton */}
            <div
              className="mb-1"
              style={{
                width: "80%",
                height: "16px",
                backgroundColor: "#e0e0e0",
                borderRadius: "4px",
                marginLeft: "auto",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            ></div>
            {/* Amount skeleton */}
            <div
              style={{
                width: "100%",
                height: "24px",
                backgroundColor: "#e0e0e0",
                borderRadius: "4px",
                marginLeft: "auto",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            ></div>
          </div>
        </div>

        {/* Percentage section skeleton */}
        <div className="d-flex align-items-center bg-light justify-content-center rounded-3 py-2 px-3">
          {/* Arrow icon skeleton */}
          <div
            className="me-1"
            style={{
              width: "16px",
              height: "16px",
              backgroundColor: "#e0e0e0",
              borderRadius: "2px",
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          ></div>

          {/* Percentage skeleton */}
          <div
            className="me-1"
            style={{
              width: "50px",
              height: "16px",
              backgroundColor: "#e0e0e0",
              borderRadius: "4px",
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          ></div>

          {/* Period text skeleton */}
          <div
            style={{
              width: "60px",
              height: "14px",
              backgroundColor: "#e0e0e0",
              borderRadius: "4px",
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          ></div>
        </div>
      </div>
    </div>

    <style>{`
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
    `}</style>
  </div>
);
